import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

export type SlaStatusFilter = 'no_sla' | 'on_time' | 'late' | 'pending';

export interface ExternalTaskFilters {
  searchInput: string;
  statusFilter: string[];
  sourceFilter: string[];
  firmFilter: string[];
  matterFilter: string[];
  assigneeFilter: string;
  needsUpdateFilter: boolean;
  linkedFilter: 'all' | 'linked' | 'unlinked';
  ordering: string | null;
  lastActivityAfter: string;
  lastActivityBefore: string;
  hasMatchedRuleFilter: boolean | null;
  hideExcludedFilter: boolean;
  slaStatusFilter: SlaStatusFilter[];
}

export interface ExternalTaskFiltersActions {
  setSearchInput: (value: string) => void;
  setStatusFilter: (value: string[]) => void;
  setSourceFilter: (value: string[]) => void;
  setFirmFilter: (value: string[]) => void;
  setMatterFilter: (value: string[]) => void;
  setAssigneeFilter: (value: string) => void;
  setNeedsUpdateFilter: (value: boolean) => void;
  setLinkedFilter: (value: 'all' | 'linked' | 'unlinked') => void;
  setOrdering: (value: string | null) => void;
  setLastActivityAfter: (value: string) => void;
  setLastActivityBefore: (value: string) => void;
  setHasMatchedRuleFilter: (value: boolean | null) => void;
  setHideExcludedFilter: (value: boolean) => void;
  setSlaStatusFilter: (value: SlaStatusFilter[]) => void;
  setAllFilters: (filters: Partial<ExternalTaskFilters>) => void;
  clearAllFilters: () => void;
}

export function useExternalTaskFilters(): [
  ExternalTaskFilters,
  ExternalTaskFiltersActions,
] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filter state from URL params
  const [searchInput, setSearchInput] = React.useState(
    searchParams.get('search') || '',
  );
  const [statusFilter, setStatusFilter] = React.useState<string[]>(
    searchParams.getAll('status'),
  );
  const [sourceFilter, setSourceFilter] = React.useState<string[]>(
    searchParams.getAll('source'),
  );
  const [firmFilter, setFirmFilter] = React.useState<string[]>(
    searchParams.getAll('firm'),
  );
  const [matterFilter, setMatterFilter] = React.useState<string[]>(
    searchParams.getAll('matter'),
  );
  const [assigneeFilter, setAssigneeFilter] = React.useState(
    searchParams.get('assignee') || '',
  );
  const [needsUpdateFilter, setNeedsUpdateFilter] = React.useState(
    searchParams.get('needs_update') === 'true',
  );
  const [linkedFilter, setLinkedFilter] = React.useState<
    'all' | 'linked' | 'unlinked'
  >((searchParams.get('linked') as 'all' | 'linked' | 'unlinked') || 'all');
  const [ordering, setOrdering] = React.useState<string | null>(
    searchParams.get('ordering') || null,
  );
  const [lastActivityAfter, setLastActivityAfter] = React.useState(
    searchParams.get('last_activity_after') || '',
  );
  const [lastActivityBefore, setLastActivityBefore] = React.useState(
    searchParams.get('last_activity_before') || '',
  );
  const [hasMatchedRuleFilter, setHasMatchedRuleFilter] = React.useState<
    boolean | null
  >(() => {
    const param = searchParams.get('has_matched_rule');
    if (param === 'true') return true;
    if (param === 'false') return false;
    return null;
  });
  const [hideExcludedFilter, setHideExcludedFilter] = React.useState(
    searchParams.get('hide_excluded') === 'true',
  );
  const [slaStatusFilter, setSlaStatusFilter] = React.useState<
    SlaStatusFilter[]
  >(searchParams.getAll('sla_status') as SlaStatusFilter[]);

  // Sync URL search params with filter state
  React.useEffect(() => {
    const newParams = new URLSearchParams();

    // Add single-value filters
    if (searchInput) newParams.set('search', searchInput);
    if (assigneeFilter) newParams.set('assignee', assigneeFilter);
    if (needsUpdateFilter) newParams.set('needs_update', 'true');
    if (linkedFilter !== 'all') newParams.set('linked', linkedFilter);
    if (ordering) newParams.set('ordering', ordering);
    if (lastActivityAfter)
      newParams.set('last_activity_after', lastActivityAfter);
    if (lastActivityBefore)
      newParams.set('last_activity_before', lastActivityBefore);
    if (hasMatchedRuleFilter !== null)
      newParams.set('has_matched_rule', hasMatchedRuleFilter.toString());
    if (hideExcludedFilter) newParams.set('hide_excluded', 'true');

    // Add multi-value filters
    statusFilter.forEach((status) => newParams.append('status', status));
    sourceFilter.forEach((source) => newParams.append('source', source));
    firmFilter.forEach((firm) => newParams.append('firm', firm));
    matterFilter.forEach((matter) => newParams.append('matter', matter));
    slaStatusFilter.forEach((sla) => newParams.append('sla_status', sla));

    setSearchParams(newParams, { replace: true });
  }, [
    searchInput,
    statusFilter,
    sourceFilter,
    firmFilter,
    matterFilter,
    assigneeFilter,
    needsUpdateFilter,
    linkedFilter,
    ordering,
    lastActivityAfter,
    lastActivityBefore,
    hasMatchedRuleFilter,
    hideExcludedFilter,
    slaStatusFilter,
    setSearchParams,
  ]);

  const setAllFilters = React.useCallback(
    (newFilters: Partial<ExternalTaskFilters>) => {
      if (newFilters.searchInput !== undefined)
        setSearchInput(newFilters.searchInput);
      if (newFilters.statusFilter !== undefined)
        setStatusFilter(newFilters.statusFilter);
      if (newFilters.sourceFilter !== undefined)
        setSourceFilter(newFilters.sourceFilter);
      if (newFilters.firmFilter !== undefined)
        setFirmFilter(newFilters.firmFilter);
      if (newFilters.matterFilter !== undefined)
        setMatterFilter(newFilters.matterFilter);
      if (newFilters.assigneeFilter !== undefined)
        setAssigneeFilter(newFilters.assigneeFilter);
      if (newFilters.needsUpdateFilter !== undefined)
        setNeedsUpdateFilter(newFilters.needsUpdateFilter);
      if (newFilters.linkedFilter !== undefined)
        setLinkedFilter(newFilters.linkedFilter);
      if (newFilters.ordering !== undefined) setOrdering(newFilters.ordering);
      if (newFilters.lastActivityAfter !== undefined)
        setLastActivityAfter(newFilters.lastActivityAfter);
      if (newFilters.lastActivityBefore !== undefined)
        setLastActivityBefore(newFilters.lastActivityBefore);
      if (newFilters.hasMatchedRuleFilter !== undefined)
        setHasMatchedRuleFilter(newFilters.hasMatchedRuleFilter);
      if (newFilters.hideExcludedFilter !== undefined)
        setHideExcludedFilter(newFilters.hideExcludedFilter);
      if (newFilters.slaStatusFilter !== undefined)
        setSlaStatusFilter(newFilters.slaStatusFilter);
    },
    [],
  );

  const clearAllFilters = React.useCallback(() => {
    setSearchInput('');
    setStatusFilter([]);
    setSourceFilter([]);
    setFirmFilter([]);
    setMatterFilter([]);
    setAssigneeFilter('');
    setNeedsUpdateFilter(false);
    setLinkedFilter('all');
    setOrdering(null);
    setLastActivityAfter('');
    setLastActivityBefore('');
    setHasMatchedRuleFilter(null);
    setHideExcludedFilter(false);
    setSlaStatusFilter([]);
  }, []);

  const filters: ExternalTaskFilters = {
    searchInput,
    statusFilter,
    sourceFilter,
    firmFilter,
    matterFilter,
    assigneeFilter,
    needsUpdateFilter,
    linkedFilter,
    ordering,
    lastActivityAfter,
    lastActivityBefore,
    hasMatchedRuleFilter,
    hideExcludedFilter,
    slaStatusFilter,
  };

  const actions: ExternalTaskFiltersActions = {
    setSearchInput,
    setStatusFilter,
    setSourceFilter,
    setFirmFilter,
    setMatterFilter,
    setAssigneeFilter,
    setNeedsUpdateFilter,
    setLinkedFilter,
    setOrdering,
    setLastActivityAfter,
    setLastActivityBefore,
    setHasMatchedRuleFilter,
    setHideExcludedFilter,
    setSlaStatusFilter,
    setAllFilters,
    clearAllFilters,
  };

  return [filters, actions];
}
